import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import {WorkspaceTemplateMode} from './types';
import {useViewerWorkspaceTemplates} from './useViewerWorkspaceTemplates';

export type WorkspaceTemplateDrawerContextType = ReturnType<
  typeof useViewerWorkspaceTemplates
> & {
  isWorkspaceTemplateDrawerOpen: boolean;
  workspaceTemplateMode: WorkspaceTemplateMode;
  closeWorkspaceTemplateDrawer: () => void;
  openWorkspaceTemplateDrawer: () => void;
};

export const WorkspaceTemplateDrawerContext = createContext<
  WorkspaceTemplateDrawerContextType | undefined
>(undefined);

type WorkspaceTemplateProps = {
  isWorkspaceTemplateActionDisplayed: boolean;
  children: React.ReactNode;
};

export const WorkspaceTemplateDrawerContextProvider = ({
  isWorkspaceTemplateActionDisplayed,
  children,
}: WorkspaceTemplateProps) => {
  const [isWorkspaceTemplateDrawerOpen, setIsWorkspaceTemplateDrawerOpen] =
    useState(false);

  const openWorkspaceTemplateDrawer = useCallback(() => {
    setIsWorkspaceTemplateDrawerOpen(true);
  }, []);

  const closeWorkspaceTemplateDrawer = useCallback(() => {
    setIsWorkspaceTemplateDrawerOpen(false);
  }, []);

  const {
    entityOrgId,
    isLoadingWorkspaceTemplates,
    refetchWorkspaceTemplates,
    workspaceTemplates,
    workspaceTemplatesError,
  } = useViewerWorkspaceTemplates(isWorkspaceTemplateActionDisplayed);

  const workspaceTemplateMode: WorkspaceTemplateMode = useMemo(() => {
    if (workspaceTemplates.length === 0) {
      return 'save';
    }
    if (workspaceTemplates.length === 1) {
      return 'update';
    }
    // we should not be hitting this since we currently only allow 1 template
    // per user within an org
    throw new Error('Invalid number of workspace templates');
  }, [workspaceTemplates]);

  const value = useMemo(
    () => ({
      closeWorkspaceTemplateDrawer,
      entityOrgId,
      isLoadingWorkspaceTemplates,
      isWorkspaceTemplateDrawerOpen,
      openWorkspaceTemplateDrawer,
      refetchWorkspaceTemplates,
      workspaceTemplateMode,
      workspaceTemplates,
      workspaceTemplatesError,
    }),
    [
      closeWorkspaceTemplateDrawer,
      entityOrgId,
      isLoadingWorkspaceTemplates,
      isWorkspaceTemplateDrawerOpen,
      openWorkspaceTemplateDrawer,
      refetchWorkspaceTemplates,
      workspaceTemplateMode,
      workspaceTemplates,
      workspaceTemplatesError,
    ]
  );

  return (
    <WorkspaceTemplateDrawerContext.Provider value={value}>
      {children}
    </WorkspaceTemplateDrawerContext.Provider>
  );
};

export const useWorkspaceTemplateDrawerContext =
  (): WorkspaceTemplateDrawerContextType => {
    const value = useContext(WorkspaceTemplateDrawerContext);

    if (value == null) {
      throw new Error(
        'useWorkspaceTemplateDrawerContext must be used within a WorkspaceTemplateDrawerContextProvider'
      );
    }

    return value;
  };
